import React from "react";

/**
 * SearchMethodSelector Component
 * Allows users to select between Aadhaar or Phone number search methods
 * Displays access status for each method with visual feedback
 *
 * @param {Object} props - Component props
 * @param {string} props.searchType - Currently selected search type ('aadhaar' or 'phone')
 * @param {Function} props.onSearchTypeChange - Callback when search type changes
 * @param {boolean} props.aadhaarAccess - Whether user has access to Aadhaar search
 * @param {boolean} props.phoneAccess - Whether user has access to Phone search
 * @returns {JSX.Element} Search method selector component
 */
export default function SearchMethodSelector({
  searchType,
  onSearchTypeChange,
  aadhaarAccess,
  phoneAccess,
}) {
  // ========================================
  // BASE STYLING
  // ========================================

  /**
   * Base card styling for search method options
   * Includes responsive layout, transitions, and border styling
   */
  const cardBase =
    "flex w-full sm:w-1/2 flex-col items-start gap-2 p-4 rounded-xl border transition-all duration-300";

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="flex flex-col sm:flex-row gap-4 w-[50%]">
      {/* ========== AADHAAR SEARCH OPTION ========== */}
      {/* Radio button and label for Aadhaar search method */}
      <label
        className={`${cardBase}
          ${
            searchType === "aadhaar"
              ? "border-indigo-500 bg-indigo-50 scale-[1.02] shadow-md"
              : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
          }
          ${aadhaarAccess ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
        `}
      >
        {/* Radio input for Aadhaar selection */}
        <div className="flex items-center gap-3 w-full">
          <input
            type="radio"
            name="searchType"
            value="aadhaar"
            checked={searchType === "aadhaar"}
            onChange={(e) => onSearchTypeChange(e.target.value)}
            disabled={!aadhaarAccess}
            className="mt-0.5 accent-indigo-600"
            aria-label="Search using Aadhaar"
          />

          {/* Aadhaar title - single line */}
          <p className="font-semibold text-gray-800">Using Aadhaar</p>
        </div>

        {/* Access status indicator below title */}
        <div className="ml-6">
          {aadhaarAccess ? (
            <p className="text-xs text-green-600 font-medium">
              ✓ Access Available
            </p>
          ) : (
            <p className="text-xs text-red-500 font-medium">✗ No Access</p>
          )}
        </div>
      </label>

      {/* ========== PHONE SEARCH OPTION ========== */}
      {/* Radio button and label for Phone search method */}
      <label
        className={`${cardBase}
          ${
            searchType === "phone"
              ? "border-indigo-500 bg-indigo-50 scale-[1.02] shadow-md"
              : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
          }
          ${phoneAccess ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
        `}
      >
        {/* Radio input for Phone selection */}
        <div className="flex items-center gap-3 w-full">
          <input
            type="radio"
            name="searchType"
            value="phone"
            checked={searchType === "phone"}
            onChange={(e) => onSearchTypeChange(e.target.value)}
            disabled={!phoneAccess}
            className="mt-0.5 accent-indigo-600"
            aria-label="Search using Phone number"
          />

          {/* Phone title - single line */}
          <p className="font-semibold text-gray-800">Using Phone</p>
        </div>

        {/* Access status indicator below title */}
        <div className="ml-6">
          {phoneAccess ? (
            <p className="text-xs text-green-600 font-medium">
              ✓ Access Available
            </p>
          ) : (
            <p className="text-xs text-red-500 font-medium">✗ No Access</p>
          )}
        </div>
      </label>
    </div>
  );
}
